import DashboardWrapper from './DashboardWrapper'
import { useState, lazy } from 'react'
import { format } from 'date-fns'
import PropTypes from 'prop-types'
import { useOutletContext, useParams } from 'react-router-dom'
import { getTeamTransactions } from '../../firestore'
import UpgradeMessage from '../UpgradeMessage'
import { Button } from '../button'
import { useModal } from '../../features/modal-manager/index.ts'

const CancelSubscriptionModal = lazy(
  () => import('../modal/CancelSubscriptionModal'),
)

export default function Billing() {
  const { teamId } = useParams()
  const { plan } = useOutletContext()

  {
    /* FREE PLAN */
  }
  if (!plan || !plan.billingCycle)
    return (
      <DashboardWrapper title="Billing">
        <PlanFree teamId={teamId} plan={plan} />
      </DashboardWrapper>
    )

  {
    /* EVENT PLAN */
  }
  if (plan.billingCycle.interval === 'once')
    return (
      <DashboardWrapper title="Billing">
        <PlanEvent teamId={teamId} plan={plan} />
      </DashboardWrapper>
    )

  {
    /* SUBSCRIPTION PLAN */
  }
  return (
    <DashboardWrapper title="Billing">
      <PlanSubscription teamId={teamId} plan={plan} />
    </DashboardWrapper>
  )
}

const PlanFree = ({ teamId, plan }) => {
  return (
    <PlanWrapper teamId={teamId} imageUrl={plan.imageUrl} planName={plan.name}>
      <UpgradeMessage message="Upgrade your account to add more features and team members." />
    </PlanWrapper>
  )
}
PlanFree.propTypes = {
  teamId: PropTypes.string,
  plan: PropTypes.object,
}

const PlanEvent = ({ teamId, plan }) => {
  return (
    <PlanWrapper
      teamId={teamId}
      imageUrl={plan.imageUrl}
      planName={`${plan.name} • ${plan.billingCycle.daysActive} Day Event`}
    >
      <p className="text-xs">
        Event ends: {format(new Date(plan.billingCycle.endsAt), 'd MMM, yyyy')}
      </p>
      {/* Plan limits */}
      <div className="flex flex-col gap-1 text-xs">
        <p>Your plan allows for {plan?.limits?.members || 1} team members.</p>
      </div>
    </PlanWrapper>
  )
}
PlanEvent.propTypes = {
  teamId: PropTypes.string,
  plan: PropTypes.object,
}

const PlanSubscription = ({ teamId, plan }) => {
  const [cancelSubscriptionModal, renderCancelSubscriptionModal] = useModal(
    CancelSubscriptionModal,
  )

  return (
    <PlanWrapper
      teamId={teamId}
      imageUrl={plan.imageUrl}
      planName={`${plan.name} plan`}
      transactionIds={plan.transactionIds}
      actionButton={
        <Button
          className="bg-transparent font-normal! text-sm text-gray-600! h-[unset] self-start p-0!"
          text="Cancel Subscription"
          onClick={cancelSubscriptionModal.show}
          disabled={plan.scheduledChange?.action === 'cancel'}
          toolTip={
            plan.scheduledChange?.action === 'cancel'
              ? 'Your subscription is already scheduled for cancelation'
              : ''
          }
          showTooltipWhenDisabled
        />
      }
    >
      {/* Plan limits */}
      <div className="flex flex-col gap-2 text-xs">
        {/* Billing interval, or cancelled */}
        {plan.scheduledChange?.action === 'cancel' ? (
          <>
            <p className="px-1 text-[10px] font-bold text-gray-100 bg-gray-700 w-max rounded-xs uppercase">
              Cancelled
            </p>
            <p className="text-xs text-gray-300">
              Subscription expires:{' '}
              {format(
                new Date(plan.scheduledChange.effective_at),
                'd MMM, yyyy',
              )}
            </p>
          </>
        ) : (
          <p>
            Billed:{' '}
            <span className="capitalize">{plan.billingCycle.interval}</span>
            ly. Next payment date:{' '}
            {format(new Date(plan.billingCycle.endsAt), 'd MMM, yyyy')}.
          </p>
        )}
        <p className="">
          <span>
            Your plan allows for {plan?.limits?.members || 1} team members. Want
            to add more?
          </span>{' '}
          <a
            href="mailto:hello@rundownstudio.app"
            className="underline hover:text-gray-300 cursor-pointer"
          >
            Email us here
          </a>
        </p>
      </div>

      {renderCancelSubscriptionModal({
        plan,
        teamId,
      })}
    </PlanWrapper>
  )
}
PlanSubscription.propTypes = {
  teamId: PropTypes.string,
  plan: PropTypes.object,
}

const PlanWrapper = ({
  teamId,
  imageUrl,
  planName,
  children,
  actionButton = <></>,
}) => {
  const [showTransactions, setShowTransactions] = useState(false)
  const [loading, setLoading] = useState(false)
  const [transactions, setTransactions] = useState()

  const handleGetTransactions = async () => {
    if (showTransactions) return setShowTransactions(false)
    setLoading(true)
    const { data } = await getTeamTransactions(teamId)
    setTransactions(data)
    setLoading(false)
    return setShowTransactions(true)
  }

  return (
    <>
      <div
        className={[
          'p-4 flex gap-4 bg-gray-100/5 border border-gray-800',
          loading && 'bg-animated from-gray-900 to-gray-800',
        ].join(' ')}
      >
        <img src={imageUrl} className="mt-1 w-12 h-12 aspect-square" />
        <div className="flex flex-col gap-1 justify-center">
          {/* Plan name */}
          <p className="font-bold">{planName}</p>

          {children}

          {/* Transactions link and list */}
          <button
            className="mt-2 text-left text-xs underline text-gray-400"
            onClick={handleGetTransactions}
          >
            View transactions
          </button>
          <div
            className={['grid text-xs gap-2', showTransactions && 'pt-4'].join(
              ' ',
            )}
          >
            {showTransactions &&
              transactions?.map((t) => {
                return <Transaction key={t.id} transaction={t} />
              })}
          </div>
        </div>
        <span className="flex-1"></span>
        {actionButton}
      </div>
    </>
  )
}
PlanWrapper.propTypes = {
  teamId: PropTypes.string,
  imageUrl: PropTypes.string,
  planName: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element),
  ]),
  actionButton: PropTypes.array,
}

const billingApiBase = import.meta.env.VITE_BILLING_FUNCTION_BASE

const Transaction = ({ transaction }) => {
  return (
    <div className="flex gap-4">
      <p className="font-semibold">
        {format(new Date(transaction.billed_at), 'd MMM, yyyy')}
      </p>
      <p>
        {(transaction.details.totals.grand_total / 100).toLocaleString(
          'en-US',
          {
            style: 'currency',
            currency: transaction.details.totals.currency_code,
          },
        )}
      </p>
      <a
        href={`${billingApiBase}/transactions/${transaction.id}/invoice`}
        className="underline"
      >
        Download invoice
      </a>
    </div>
  )
}

Transaction.propTypes = {
  transaction: PropTypes.object,
}
